import {
  pgTable,
  text,
  timestamp,
  uuid,
  integer,
  boolean,
  jsonb,
  index,
  uniqueIndex,
  check,
} from "drizzle-orm/pg-core";
import { sql } from "drizzle-orm";
import { pages, pageVersions } from "./page-builder";

// ---------------------------------------------------------------------------
// Timestamp helper
// ---------------------------------------------------------------------------

const tz = { withTimezone: true } as const;

// ---------------------------------------------------------------------------
// JSONB type interfaces
// ---------------------------------------------------------------------------

export interface BrandColors {
  primary?: string;
  secondary?: string;
  accent?: string;
  text?: string;
  background?: string;
  [key: string]: unknown;
}

export interface BrandFont {
  family: string;
  weight?: string;
  [key: string]: unknown;
}

export interface BrandFonts {
  headings?: BrandFont;
  body?: BrandFont;
  [key: string]: unknown;
}

export interface SocialLinks {
  facebook?: string;
  instagram?: string;
  linkedin?: string;
  twitter?: string;
  tiktok?: string;
  youtube?: string;
  [key: string]: unknown;
}

export interface FieldRegistryOptions {
  choices?: Array<{ value: string; label: string }>;
  [key: string]: unknown;
}

export interface SavedSectionPuckData {
  content?: Array<{ type: string; props?: Record<string, unknown> }>;
  root?: Record<string, unknown>;
  [key: string]: unknown;
}

// ---------------------------------------------------------------------------
// Status/type enums
// ---------------------------------------------------------------------------

export const FIELD_SOURCES = [
  "crm_field",
  "crm_pipeline",
  "product",
  "calendar",
  "automation",
] as const;

export const FIELD_DATA_TYPES = [
  "text",
  "email",
  "phone",
  "select",
  "date",
  "number",
  "boolean",
] as const;

export type FieldSource = (typeof FIELD_SOURCES)[number];
export type FieldDataType = (typeof FIELD_DATA_TYPES)[number];

// ---------------------------------------------------------------------------
// 1. Field Registries
// ---------------------------------------------------------------------------

export const fieldRegistries = pgTable(
  "field_registries",
  {
    id: uuid("id").primaryKey().defaultRandom(),
    tenantId: uuid("tenant_id").notNull(),
    source: text("source").notNull(),
    sourceId: text("source_id").notNull(),
    label: text("label").notNull(),
    dataType: text("data_type").notNull(),
    options: jsonb("options").$type<FieldRegistryOptions>(),
    isRequired: boolean("is_required").notNull().default(false),
    metadata: jsonb("metadata")
      .$type<Record<string, unknown>>()
      .default({}),
    createdAt: timestamp("created_at", tz).notNull().defaultNow(),
    updatedAt: timestamp("updated_at", tz).notNull().defaultNow(),
  },
  (table) => [
    uniqueIndex("idx_field_registries_tenant_source_id").on(
      table.tenantId,
      table.source,
      table.sourceId,
    ),
    index("idx_field_registries_tenant").on(table.tenantId),
    check(
      "chk_field_registries_source",
      sql`${table.source} IN ('crm_field', 'crm_pipeline', 'product', 'calendar', 'automation')`,
    ),
    check(
      "chk_field_registries_data_type",
      sql`${table.dataType} IN ('text', 'email', 'phone', 'select', 'date', 'number', 'boolean')`,
    ),
  ],
);

// ---------------------------------------------------------------------------
// 2. Workspace Brand Settings
// ---------------------------------------------------------------------------

export const workspaceBrandSettings = pgTable(
  "workspace_brand_settings",
  {
    id: uuid("id").primaryKey().defaultRandom(),
    tenantId: uuid("tenant_id").notNull(),
    logoUrl: text("logo_url"),
    iconUrl: text("icon_url"),
    colors: jsonb("colors").$type<BrandColors>().default({}),
    fonts: jsonb("fonts").$type<BrandFonts>().default({}),
    businessName: text("business_name"),
    industry: text("industry"),
    brandVoice: text("brand_voice"),
    targetAudience: text("target_audience"),
    tagline: text("tagline"),
    socialLinks: jsonb("social_links").$type<SocialLinks>().default({}),
    guidelinesUrl: text("guidelines_url"),
    createdAt: timestamp("created_at", tz).notNull().defaultNow(),
    updatedAt: timestamp("updated_at", tz).notNull().defaultNow(),
  },
  (table) => [
    uniqueIndex("idx_brand_settings_tenant").on(table.tenantId),
  ],
);

// ---------------------------------------------------------------------------
// 3. Form Submissions
// ---------------------------------------------------------------------------

export const formSubmissions = pgTable(
  "form_submissions",
  {
    id: uuid("id").primaryKey().defaultRandom(),
    tenantId: uuid("tenant_id").notNull(),
    pageId: uuid("page_id")
      .notNull()
      .references(() => pages.id, { onDelete: "cascade" }),
    versionId: uuid("version_id")
      .references(() => pageVersions.id, { onDelete: "set null" }),
    componentId: text("component_id"),
    data: jsonb("data").$type<Record<string, unknown>>().notNull(),
    bindings: jsonb("bindings")
      .$type<Record<string, unknown>>()
      .default({}),
    contactId: text("contact_id"),
    contactEmail: text("contact_email"),
    ipAddressHash: text("ip_address_hash"), // HMAC-SHA-256 hash (IP_HASH_SECRET) — never raw IP
    userAgent: text("user_agent"),
    referrer: text("referrer"),
    utmSource: text("utm_source"),
    utmMedium: text("utm_medium"),
    utmCampaign: text("utm_campaign"),
    consentGiven: boolean("consent_given"),
    consentText: text("consent_text"),
    funnelSessionId: uuid("funnel_session_id"),
    funnelStepId: uuid("funnel_step_id"),
    abExperimentId: uuid("ab_experiment_id"),
    abVariantId: uuid("ab_variant_id"),
    affiliateCode: text("affiliate_code"), // 2B.13: Tracks which affiliate referred the submission
    processedAt: timestamp("processed_at", tz),
    createdAt: timestamp("created_at", tz).notNull().defaultNow(),
  },
  (table) => [
    index("idx_form_submissions_tenant_page").on(
      table.tenantId,
      table.pageId,
    ),
    index("idx_form_submissions_tenant").on(table.tenantId),
    index("idx_form_submissions_page_created").on(
      table.pageId,
      table.createdAt,
    ),
    index("idx_form_submissions_contact").on(
      table.tenantId,
      table.contactId,
    ),
    index("idx_form_submissions_contact_email").on(
      table.tenantId,
      table.contactEmail,
    ),
  ],
);

// ---------------------------------------------------------------------------
// GDPR Erasure Log — proof of compliance for Art. 17 accountability
// ---------------------------------------------------------------------------

export const gdprErasureLog = pgTable(
  "gdpr_erasure_log",
  {
    id: uuid("id").primaryKey().defaultRandom(),
    tenantId: uuid("tenant_id").notNull(),
    subjectIdentifier: text("subject_identifier").notNull(),
    identifierType: text("identifier_type").notNull(),
    tablesAffected: jsonb("tables_affected")
      .$type<Record<string, number>>()
      .notNull(),
    requestedBy: text("requested_by").notNull(),
    completedAt: timestamp("completed_at", tz).notNull().defaultNow(),
  },
  (table) => [
    index("idx_erasure_log_tenant").on(table.tenantId, table.subjectIdentifier),
  ],
);

// ---------------------------------------------------------------------------
// 4. Page Assets
// ---------------------------------------------------------------------------

export const pageAssets = pgTable(
  "page_assets",
  {
    id: uuid("id").primaryKey().defaultRandom(),
    tenantId: uuid("tenant_id").notNull(),
    siteId: uuid("site_id"),
    filename: text("filename").notNull(),
    r2Key: text("r2_key").notNull(),
    contentType: text("content_type").notNull(),
    sizeBytes: integer("size_bytes").notNull(),
    width: integer("width"),
    height: integer("height"),
    uploadedBy: text("uploaded_by").notNull(),
    createdAt: timestamp("created_at", tz).notNull().defaultNow(),
  },
  (table) => [
    uniqueIndex("idx_page_assets_r2_key").on(table.r2Key),
    index("idx_page_assets_tenant").on(table.tenantId),
  ],
);

// ---------------------------------------------------------------------------
// 5. Saved Sections
// ---------------------------------------------------------------------------

export const savedSections = pgTable(
  "saved_sections",
  {
    id: uuid("id").primaryKey().defaultRandom(),
    tenantId: uuid("tenant_id").notNull(),
    name: text("name").notNull(),
    category: text("category").notNull().default("custom"),
    puckData: jsonb("puck_data").$type<SavedSectionPuckData>().notNull(),
    thumbnail: text("thumbnail"),
    usageCount: integer("usage_count").notNull().default(0),
    createdBy: text("created_by").notNull(),
    createdAt: timestamp("created_at", tz).notNull().defaultNow(),
    updatedAt: timestamp("updated_at", tz).notNull().defaultNow(),
  },
  (table) => [
    index("idx_saved_sections_tenant").on(table.tenantId),
    index("idx_saved_sections_tenant_created").on(table.tenantId, table.createdAt),
  ],
);

// ---------------------------------------------------------------------------
// 6. Style Presets
// ---------------------------------------------------------------------------

export const stylePresets = pgTable(
  "style_presets",
  {
    id: uuid("id").primaryKey().defaultRandom(),
    tenantId: uuid("tenant_id").notNull(),
    name: text("name").notNull(),
    category: text("category").notNull().default("custom"),
    styleData: jsonb("style_data").$type<Record<string, unknown>>().notNull(),
    createdBy: text("created_by").notNull(),
    createdAt: timestamp("created_at", tz).notNull().defaultNow(),
    updatedAt: timestamp("updated_at", tz).notNull().defaultNow(),
  },
  (table) => [
    index("idx_style_presets_tenant").on(table.tenantId),
    index("idx_style_presets_tenant_created").on(table.tenantId, table.createdAt),
  ],
);
